import React, { useEffect, useRef } from 'react';

type ParticleConstellationProps = {
    className?: string;
    particleCount?: number;
    connectionDistance?: number;
    particleRadius?: number;
    particleColor?: string;
    lineColor?: string;
    speed?: number;
    mouseRadius?: number;
};

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    opacity: number;
}

export default function DottedGlowBackground({
    className,
    particleCount = 80,
    connectionDistance = 150,
    particleRadius = 1.5,
    particleColor = "rgba(255,255,255,0.6)",
    lineColor = "255,255,255",
    speed = 0.3,
    mouseRadius = 200,
}: ParticleConstellationProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mouseRef = useRef<{ x: number; y: number; active: boolean }>({ x: -9999, y: -9999, active: false });

    useEffect(() => {
        const el = canvasRef.current;
        const container = containerRef.current;
        if (!el || !container) return;

        const ctx = el.getContext("2d");
        if (!ctx) return;

        let raf = 0;
        let stopped = false;
        let particles: Particle[] = [];
        let width = 0;
        let height = 0;

        const dpr = Math.max(1, window.devicePixelRatio || 1);

        const resize = () => {
            const rect = container.getBoundingClientRect();
            width = rect.width;
            height = rect.height;
            el.width = Math.max(1, Math.floor(width * dpr));
            el.height = Math.max(1, Math.floor(height * dpr));
            el.style.width = `${width}px`;
            el.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const initParticles = () => {
            particles = [];
            for (let i = 0; i < particleCount; i++) {
                const angle = Math.random() * Math.PI * 2;
                const v = speed * (0.3 + Math.random() * 0.7);
                particles.push({
                    x: Math.random() * width,
                    y: Math.random() * height,
                    vx: Math.cos(angle) * v,
                    vy: Math.sin(angle) * v,
                    radius: particleRadius * (0.5 + Math.random() * 1),
                    opacity: 0.2 + Math.random() * 0.5,
                });
            }
        };

        const ro = new ResizeObserver(() => {
            resize();
            if (particles.length === 0) initParticles();
        });
        ro.observe(container);
        setTimeout(() => {
            resize();
            initParticles();
        }, 0);

        // Mouse tracking
        const onMouseMove = (e: MouseEvent) => {
            const rect = container.getBoundingClientRect();
            mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top, active: true };
        };
        const onMouseLeave = () => {
            mouseRef.current.active = false;
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseleave", onMouseLeave);

        const draw = () => {
            if (stopped) return;

            ctx.clearRect(0, 0, width, height);

            const mouse = mouseRef.current;

            // Update positions
            for (const p of particles) {
                p.x += p.vx;
                p.y += p.vy;

                // Wrap around edges with padding
                if (p.x < -50) p.x = width + 50;
                if (p.x > width + 50) p.x = -50;
                if (p.y < -50) p.y = height + 50;
                if (p.y > height + 50) p.y = -50;

                // Gentle mouse repulsion
                if (mouse.active) {
                    const dx = p.x - mouse.x;
                    const dy = p.y - mouse.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < mouseRadius && dist > 0) {
                        const force = (1 - dist / mouseRadius) * 0.02;
                        p.vx += (dx / dist) * force;
                        p.vy += (dy / dist) * force;
                    }
                }

                // Speed damping to prevent runaway velocity
                const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
                const maxSpd = speed * 2;
                if (spd > maxSpd) {
                    p.vx = (p.vx / spd) * maxSpd;
                    p.vy = (p.vy / spd) * maxSpd;
                }
            }

            // Draw connections
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const a = particles[i];
                    const b = particles[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < connectionDistance) {
                        const alpha = (1 - dist / connectionDistance) * 0.15;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.strokeStyle = `rgba(${lineColor},${alpha})`;
                        ctx.lineWidth = 0.5;
                        ctx.stroke();
                    }
                }
            }

            // Draw mouse connections
            if (mouse.active) {
                for (const p of particles) {
                    const dx = p.x - mouse.x;
                    const dy = p.y - mouse.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < mouseRadius) {
                        const alpha = (1 - dist / mouseRadius) * 0.25;
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(mouse.x, mouse.y);
                        ctx.strokeStyle = `rgba(${lineColor},${alpha})`;
                        ctx.lineWidth = 0.8;
                        ctx.stroke();
                    }
                }
            }

            // Draw particles
            ctx.shadowColor = "rgba(255,255,255,0.3)";
            for (const p of particles) {
                // Brighter near mouse
                let glowBoost = 0;
                if (mouse.active) {
                    const dx = p.x - mouse.x;
                    const dy = p.y - mouse.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < mouseRadius) {
                        glowBoost = (1 - dist / mouseRadius) * 0.5;
                    }
                }

                const finalOpacity = Math.min(1, p.opacity + glowBoost);
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius + glowBoost * 1.5, 0, Math.PI * 2);
                ctx.fillStyle = particleColor;
                ctx.globalAlpha = finalOpacity;
                ctx.shadowBlur = glowBoost > 0.2 ? 8 : 0;
                ctx.fill();
            }

            ctx.globalAlpha = 1;
            ctx.shadowBlur = 0;

            raf = requestAnimationFrame(draw);
        };

        raf = requestAnimationFrame(draw);

        return () => {
            stopped = true;
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseleave", onMouseLeave);
            ro.disconnect();
        };
    }, [particleCount, connectionDistance, particleRadius, particleColor, lineColor, speed, mouseRadius]);

    return (
        <div ref={containerRef} className={className} style={{ position: "fixed", inset: 0, zIndex: -1, pointerEvents: "none" }}>
            <canvas ref={canvasRef} style={{ display: "block" }} />
        </div>
    );
}
